/* eslint-disable react-hooks/exhaustive-deps */
import DialogTitle from '@mui/material/DialogTitle'
import Dialog from '@mui/material/Dialog'
import { useOrgan } from '../../hooks/useOrgan'
import { organizationPrimaryColors } from '../../layouts/PermanentSidebarMUI'
import { Box, DialogContent, TextField, Typography } from '@mui/material'
import SimpleBody from '../../components/forms/SimpleBody'
import { addItemsMaintenance } from '../../constants/forms/manutencao/maintenanceFields'
import {
  cleanPayload,
  normalizeFormErrors,
  setDefaultFieldValues,
  objectToFormData,
} from '../../utils/objectUtils'
import useBaseService from '../../hooks/services/useBaseService'
import { useNavigate } from 'react-router-dom'
import Form from '../../components/forms/Form'
import useForm from '../../hooks/useForm'
import useRemoteSelectOptions from '../../hooks/useRemoteSelectOptions'
import { entities } from '../../constants/entities'
import { useCallback, useEffect, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import useUrlFilters from '../../hooks/useUrlFilters'
import { useSectionFields } from '../../hooks/useSectionFields'
import useSelectOptions from '../../hooks/useSelectOptions'
import { transformFiltersForUrl } from '../../utils/transformFiltersForUrl'
import { TechnicianStatus } from '../../enums/technician_status-enum'
import useQueryWithLoader from '../../hooks/useQueryWithLoader'
import useNotification from '../../hooks/useNotification'
import { EquipmentEnum } from '../../enums/equipment-enum'

export default function AddItemMaintenanceModal(props) {
  const { maintenance, onClose, open } = props
  const navigate = useNavigate()
  const { selectedOrgan } = useOrgan()
  const { updateField: updateProps, removeFields } = useSectionFields()

  const { showNotification } = useNotification()

  const primaryColor = selectedOrgan?.name
    ? organizationPrimaryColors[selectedOrgan.name.toUpperCase()] ||
      organizationPrimaryColors.ALUCOM
    : organizationPrimaryColors.ALUCOM

  const { filters, pagination } = useUrlFilters(
    {
      deleted_at: false,
    },
    'maintenance'
  )

  const queryClient = useQueryClient()
  const {
    post: baseServicePost,
    get: baseServiceGet,
    formErrors,
    setFormErrors,
  } = useBaseService(navigate)

  const [addItemsMaintenanceSection] = addItemsMaintenance
  const { formData, handleChange, resetForm } = useForm(
    setDefaultFieldValues([addItemsMaintenanceSection]),
    [addItemsMaintenanceSection]
  )
  const [fields, setFields] = useState(addItemsMaintenanceSection.fields)
  const [selectedServiceFile, setSelectedServiceFile] = useState(null)

  const maintenanceTypeSelect = useRemoteSelectOptions({
    queryKey: ['maintenanceTypeOptions'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      })
      const params = { filters, page, perPage }

      const response = await baseServiceGet(
        entities.maintenanceTypes.get,
        params
      )
      const hiddenTypes = new Set([
        'REVISÃO_MANUTENÇÃO_OTRS',
        'MANUTENÇÃO_CORRETIVA',
        'MANUTENÇÃO_PREVENTIVA',
      ])
      const result = response.result
      if (Array.isArray(result?.data)) {
        return {
          ...result,
          data: result.data.filter(
            (type) => !hiddenTypes.has(type.name?.trim()?.toUpperCase())
          ),
        }
      }
      return Array.isArray(result)
        ? result.filter(
            (type) => !hiddenTypes.has(type.name?.trim()?.toUpperCase())
          )
        : result
    },
    fieldId: 'items.maintenance_type_id',
    formData,
    isMulti: false,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (maintenanceType) => ({
      value: maintenanceType.id,
      label:
        maintenanceType.name?.trim()?.toUpperCase() === 'UPGRADE'
          ? 'UPGRADE/DOWNGRADE'
          : maintenanceType.name,
    }),
  })

  const selectedMaintenanceTypeName = String(
    maintenanceTypeSelect.selectedValue?.label ??
      maintenanceTypeSelect.selectedValue?.name ??
      ''
  ).trim().toUpperCase()
  const isUpgradeDowngrade = selectedMaintenanceTypeName === 'UPGRADE/DOWNGRADE'
  const visibleFields = isUpgradeDowngrade
    ? fields
        .filter((field) =>
          ['items.maintenance_type_id', 'items.details'].includes(field.id)
        )
        .map((field) =>
          field.id === 'items.details'
            ? {
                ...field,
                label: 'Observações',
                placeholder:
                  'Descreve as mudanças do equipamento e de que se trata, um downgrade ou upgrade',
              }
            : field
        )
    : fields

  const maintenanceStatusSelect = useRemoteSelectOptions({
    queryKey: ['maintenanceStatusOptions'],
    fetcher: async ({ page = 1, perPage = 15, search = '' }) => {
      const filters = transformFiltersForUrl({
        deleted_at: false,
        ...(search && { name: search }),
      })
      const params = { filters, page, perPage }

      const response = await baseServiceGet(
        entities.maintenanceStatus.get,
        params
      )
      const result = response.result
      if (Array.isArray(result?.data)) {
        return {
          ...result,
          data: result.data.filter(
            (status) => status.name?.trim()?.toUpperCase() !== 'EM ANDAMENTO'
          ),
        }
      }
      return Array.isArray(result)
        ? result.filter(
            (status) => status.name?.trim()?.toUpperCase() !== 'EM ANDAMENTO'
          )
        : result
    },
    fieldId: 'items.maintenance_status_id',
    formData,
    isMulti: false,
    perPage: 15,
    initialPage: 1,
    enablePagination: true,
    mapToOption: (maintenanceStatus) => ({
      value: maintenanceStatus.id,
      label: maintenanceStatus.name,
    }),
  })

  const { data: technicianStatusOptions } = useQueryWithLoader({
    queryKey: ['technicianStatusOptions'],
    queryFn: async () => {
      return await baseServiceGet(entities.technicianStatus.get, {
        deleted_at: false,
        perPage: 1000,
      }).then((res) => res.result.data)
    },
  })

  const [technicianStatusOptionsState, setTechnicianStatusOptionsState] =
    useState([])

  const technicianStatusSelect = useSelectOptions({
    fieldId: 'items.technician_status_id',
    rawOptions: technicianStatusOptionsState,
    formData,
    isMulti: false,
    mapToOption: (technicianStatus) => ({
      value: technicianStatus.id,
      label: technicianStatus.name,
    }),
  })

  const selects = {
    'items.maintenance_type_id': maintenanceTypeSelect,
    'items.maintenance_status_id': maintenanceStatusSelect,
    'items.technician_status_id': technicianStatusSelect,
  }

  // * Bloqueiar campos dinamicamente, para forçar o usuário a selecionar opções por vez
  useEffect(() => {
    let updatedItemsFields = [...addItemsMaintenanceSection.fields]

    if (isUpgradeDowngrade) {
      setFields(updatedItemsFields)
      return
    }

    // * Se o campo items.maintenance_type_id não tiver preenchido, bloqueie os demais campos
    if (Boolean(maintenanceTypeSelect.selectedValue?.value) === false) {
      updatedItemsFields = updateProps({
        arrayDependente: updatedItemsFields,
        field_id: addItemsMaintenanceSection.fields
          .filter((item) => item.id !== 'items.maintenance_type_id')
          .map((field) => field.id),
        updates: {
          disabled: true,
        },
      })

      setFields(updatedItemsFields)
    }

    // * Se o campo items.maintenance_status_id não tiver preenchido, bloqueie os demais campos
    if (Boolean(maintenanceStatusSelect.selectedValue?.value) === false) {
      updatedItemsFields = updateProps({
        arrayDependente: updatedItemsFields,
        field_id: addItemsMaintenanceSection.fields
          .filter(
            (item) =>
              item.id !== 'items.maintenance_type_id' &&
              item.id !== 'items.maintenance_status_id'
          )
          .map((field) => field.id),
        updates: {
          disabled: true,
        },
      })

      setFields(updatedItemsFields)
    }

    // * Se o campo items.technician_status_id não tiver preenchido, bloqueie os demais campos
    if (Boolean(technicianStatusSelect.selectedValue?.value) === false) {
      updatedItemsFields = updateProps({
        arrayDependente: updatedItemsFields,
        field_id: addItemsMaintenanceSection.fields
          .filter(
            (item) =>
              item.id !== 'items.maintenance_type_id' &&
              item.id !== 'items.maintenance_status_id' &&
              item.id !== 'items.technician_status_id' &&
              item.id !== 'items.details'
          )
          .map((field) => field.id),
        updates: {
          disabled: true,
        },
      })

      setFields(updatedItemsFields)
    }
  }, [maintenanceTypeSelect, isUpgradeDowngrade])

  const getSelectMeta = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      if (!selectConfig) return {}

      return {
        hasNextPage: selectConfig.hasNextPage,
        isFetchingNextPage: selectConfig.isFetchingNextPage,
        isLoading: selectConfig.isLoading,
        isFetching: selectConfig.isFetching,
        handleMenuScrollToBottom: selectConfig.handleMenuScrollToBottom,
        handleSearch: selectConfig.handleSearch,
        onFocus: selectConfig.activateQuery,
      }
    },
    [selects]
  )

  // --- Funções para Selects (getOptions e getSelectedValue) ---
  const getOptions = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.options || []
    },
    [selects]
  )

  const getSelectedValue = useCallback(
    (fieldId) => {
      const selectConfig = selects[fieldId]
      return selectConfig?.selectedValue || null
    },
    [selects]
  )

  const createMaintenanceItemMutation = useMutation({
    mutationFn: async (data) => {
      const response = await baseServicePost(
        entities.maintenances.itemCreate(maintenance?.id),
        data
      )
      return response
    },
    onSuccess: (response) => {
      if (response?.status === 201) {
        onClose()
        setSelectedServiceFile(null)
        queryClient.invalidateQueries(['maintenance', filters, pagination])
        queryClient.invalidateQueries({
          queryKey: ['maintenance_items', maintenance?.id],
        })
      }
    },
    onError: (error) => {
      console.error('onError ', error)
      if (error.response?.data?.errors) {
        setFormErrors(error.response.data.errors)
      }
    },
  })

  const handleSubmit = async () => {
    try {
      if (!formData.items.details) {
        showNotification('warning', 'Preencha todos os campos')
        return
      }
      const technicianStatusId = Number(
        formData.items.technician_status_id?.value ??
          formData.items.technician_status_id
      )
      if (isUpgradeDowngrade) {
        const cleanedData = cleanPayload(formData)
        const payload = {
          items: {
            maintenance_type_id: Number(
              formData.items.maintenance_type_id?.value ??
                formData.items.maintenance_type_id
            ),
            details: cleanedData.items.details,
          },
        }
        return await createMaintenanceItemMutation.mutateAsync(
          objectToFormData(payload)
        )
      }
      const needsDocument = [
        TechnicianStatus.WARRANTY.value,
        TechnicianStatus.ASSESSMENT.value,
      ].includes(technicianStatusId)

      if (
        technicianStatusId === TechnicianStatus.WARRANTY.value &&
        (!Number.isInteger(Number(formData.items.warranty_days)) ||
          Number(formData.items.warranty_days) < 1 ||
          Number(formData.items.warranty_days) > 3650)
      ) {
        showNotification('warning', 'Informe um prazo de garantia entre 1 e 3650 dias.')
        return
      }

      if (needsDocument && !selectedServiceFile) {
        showNotification(
          'warning',
          'Anexe um documento para registrar um serviço de laudo ou garantia.'
        )
        return
      }

      const cleanedData = cleanPayload(formData)
      const payload = {
        items: {
          ...cleanedData.items,
          ...(selectedServiceFile ? { file: selectedServiceFile } : {}),
        },
      }
      return await createMaintenanceItemMutation.mutateAsync(
        objectToFormData(payload)
      )
    } catch (error) {
      console.error('Erro ao enviar item de manutenção:', {
        status: error.status,
        message: error.message,
      })
    }
  }

  const handleClose = () => {
    onClose()
    resetForm()
    setSelectedServiceFile(null)
  }

  const normalizedFormErrors = normalizeFormErrors(formErrors)
  const warrantyDaysError = normalizedFormErrors.items?.warranty_days
  const warrantyDaysErrorMessage = Array.isArray(warrantyDaysError)
    ? warrantyDaysError[0]
    : warrantyDaysError

  // Filtrar technicianStatusOptions dinamicamente
  useEffect(() => {
    switch (Number(formData.items.maintenance_status_id?.value)) {
      case 1: // Manutenção Em Andamento
        setTechnicianStatusOptionsState(
          technicianStatusOptions.filter((option) =>
            [
              TechnicianStatus.NOT_RELEASED.label,
              TechnicianStatus.AWAITING_PARTS.label,
              TechnicianStatus.ASSESSMENT.label,
            ].includes(option.name)
          )
        )
        break
      case 2: // Manutenção Concluida
        const findEquipment = maintenance?.equipmentData
        setTechnicianStatusOptionsState(
          technicianStatusOptions.filter((option) =>
            [
              TechnicianStatus.RELEASED.label,
              TechnicianStatus.WITHOUT_REPAIR.label,
              TechnicianStatus.WARRANTY.label,
              // * Permitir USO INTERNO se o equipamento estiver disponível para locação
              ...([EquipmentEnum.AVAILABLE].includes(findEquipment?.status_id)
                ? [TechnicianStatus.INTERNAL_USE.label]
                : []),
            ].includes(option.name)
          )
        )
        break
      default:
        setTechnicianStatusOptionsState(technicianStatusOptions)
        break
    }
  }, [formData.items.maintenance_status_id?.value, technicianStatusOptions])

  // Remover campos de forma dinamica
  useEffect(() => {
    let updatedFields = [...addItemsMaintenanceSection.fields]

    if (maintenance && maintenance.equipment_id) {
      updatedFields = removeFields({
        arrayDependente: updatedFields,
        field_id: ['maintenance.product_item_id'],
      })
    }

    setFields(updatedFields)
  }, [
    addItemsMaintenanceSection.fields,
    maintenance,
    removeFields,
    technicianStatusSelect.selectedValue,
  ])

  return (
    <Dialog onClose={handleClose} open={open} fullWidth maxWidth="md">
      <DialogTitle
        sx={{
          backgroundColor: primaryColor,
          color: '#FFFFFF',
          fontWeight: 'bold',
        }}>
        Adicionar Serviço a Manutenção
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 4 }}>
          <Form
            initialFormData={{ ...formData }}
            onSubmit={handleSubmit}
            textSubmit="Adicionar Serviço"
            textLoadingSubmit="Adicionando Serviço..."
            showButtonBack={false}
            isLoading={false}>
            {() => (
              <>
                <div key={fields.section}>
                  <SimpleBody
                    fields={visibleFields}
                    formErrors={normalizedFormErrors}
                    formData={formData}
                    handleFieldChange={(fieldId, value) => {
                      if (fieldId === 'items.technician_status_id') {
                        setSelectedServiceFile(null)
                        const selectedStatusId = Number(value?.value ?? value)
                        if (selectedStatusId !== TechnicianStatus.WARRANTY.value) {
                          handleChange('items.warranty_days', '')
                        }
                      }
                      if (fieldId === 'items.maintenance_type_id') {
                        handleChange('items.maintenance_status_id', '')
                        handleChange('items.technician_status_id', '')
                        handleChange('items.warranty_days', '')
                        setSelectedServiceFile(null)
                      }
                      handleChange(fieldId, value)
                    }}
                    getOptions={getOptions}
                    getSelectedValue={getSelectedValue}
                    getSelectMeta={getSelectMeta}
                  />
                  {Number(
                    formData.items.technician_status_id?.value ??
                      formData.items.technician_status_id
                  ) === TechnicianStatus.WARRANTY.value && (
                    <TextField
                      fullWidth
                      required
                      type="number"
                      label="Prazo para retorno (dias)"
                      placeholder="Ex.: 30"
                      value={formData.items.warranty_days ?? ''}
                      onChange={(event) =>
                        handleChange('items.warranty_days', event.target.value)
                      }
                      inputProps={{ min: 1, max: 3650, step: 1 }}
                      error={Boolean(warrantyDaysErrorMessage)}
                      helperText={
                        warrantyDaysErrorMessage ||
                        'Informe em quantos dias o equipamento deve retornar da garantia.'
                      }
                      sx={{ mt: 2 }}
                    />
                  )}
                  {[
                    TechnicianStatus.WARRANTY.value,
                    TechnicianStatus.ASSESSMENT.value,
                  ].includes(
                    Number(
                      formData.items.technician_status_id?.value ??
                        formData.items.technician_status_id
                    )
                  ) && (
                    <Box sx={{ mt: 2 }}>
                      <Typography variant="body2" sx={{ mb: 1 }}>
                        Anexo obrigatório para serviço de{' '}
                        {Number(
                          formData.items.technician_status_id?.value ??
                            formData.items.technician_status_id
                        ) === TechnicianStatus.WARRANTY.value
                          ? 'garantia'
                          : 'laudo'}
                        .
                      </Typography>
                      <input
                        type="file"
                        accept=".pdf,.doc,.docx,.jpg,.jpeg,.png"
                        onChange={(event) =>
                          setSelectedServiceFile(event.target.files?.[0] ?? null)
                        }
                      />
                      {selectedServiceFile && (
                        <Typography variant="caption" display="block">
                          {selectedServiceFile.name}
                        </Typography>
                      )}
                    </Box>
                  )}
                </div>
              </>
            )}
          </Form>
        </Box>
      </DialogContent>
    </Dialog>
  )
}
